'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { IPlugin } from '@/types';
import { CSSVars } from '@/styles/styles.config';
import {
  StyledHero,
  StyledStatBar,
  StyledHeroCta,
  StyledSection,
  StyledInner,
  StyledSectionLabel,
  StyledSectionHeading,
  StyledSectionIntro,
  StyledWidgetGrid,
  StyledWidgetCard,
  StyledSectionCta,
  StyledBenefitsList,
  StyledBenefitItem,
  StyledPlatformCarousel,
  StyledCtaBanner,
} from './UseCasePage.styles';

const PLATFORM_LOGOS = [
  { name: 'Shopify', slug: 'shopify', file: 'Shopify.png', width: 140 },
  { name: 'WordPress', slug: 'wordpress', file: 'Wordpress.png', width: 176 },
  { name: 'Wix', slug: 'wix', file: 'Wix.png', width: 102 },
  { name: 'Webflow', slug: 'webflow', file: 'Webflow.png', width: 160 },
  { name: 'Squarespace', slug: 'squarespace', file: 'Squarespace.png', width: 216 },
  { name: 'BigCommerce', slug: 'bigcommerce', file: 'Bigcommerce.png', width: 176 },
  { name: 'Weebly', slug: 'weebly', file: 'Weebly.png', width: 116 },
  { name: 'Joomla', slug: 'joomla', file: 'Joomla.png', width: 196 },
  { name: 'Duda', slug: 'duda', file: 'Duda.png', width: 94 },
  { name: 'Magento', slug: 'magento', file: 'Magento.png', width: 144 },
  { name: 'Elementor', slug: 'elementor', file: 'Elementor.png', width: 160 },
  { name: 'Unbounce', slug: 'unbounce', file: 'Unbounce.png', width: 174 },
];

interface IWidgetDescription {
  slug: string;
  description: string;
}

interface IProps {
  heroHighlight: string;
  heroRest: string;
  tagline: string;
  featuredWidgets: IPlugin[];
  widgetDescriptions: IWidgetDescription[];
  benefits: string[];
  ctaHeading: string;
}

export const UseCasePage = ({
  heroHighlight,
  heroRest,
  tagline,
  featuredWidgets,
  widgetDescriptions,
  benefits,
  ctaHeading,
}: IProps) => {
  return (
    <>
      {/* Hero */}
      <StyledHero>
        <h1>
          <span className="highlight">{heroHighlight}</span> {heroRest}
        </h1>
        <p>{tagline}</p>
        <StyledStatBar>
          <span>500K+ Businesses</span>
          <span>200+ Widgets</span>
          <span>200+ Platforms</span>
          <span>No Coding Required</span>
        </StyledStatBar>
        <StyledHeroCta as={Link} href="/signup">
          Start for Free
        </StyledHeroCta>
      </StyledHero>

      {/* Featured Widgets */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>FEATURED WIDGETS</StyledSectionLabel>
          <StyledSectionHeading>
            The right widgets for your needs
          </StyledSectionHeading>
          <StyledSectionIntro>
            Each widget is fully customizable, responsive, and embeds on any
            platform in seconds. Free to start, no coding required.
          </StyledSectionIntro>
          <StyledWidgetGrid>
            {featuredWidgets.map((widget) => {
              const desc = widgetDescriptions.find(
                (d) => d.slug === widget.slug
              );
              return (
                <StyledWidgetCard key={widget.slug}>
                  {widget.logo ? (
                    <Image
                      src={widget.logo}
                      alt={widget.name}
                      width={56}
                      height={56}
                    />
                  ) : (
                    <div className="widget-icon-placeholder">W</div>
                  )}
                  <h3>{widget.name}</h3>
                  <p>{desc?.description || widget.teaser || ''}</p>
                  <Link href={`/widgets/${widget.slug}`}>
                    Try {widget.name} Free
                  </Link>
                </StyledWidgetCard>
              );
            })}
          </StyledWidgetGrid>
          <StyledSectionCta as={Link} href="/widgets" $center>
            See All Widgets
          </StyledSectionCta>
        </StyledInner>
      </StyledSection>

      {/* Benefits */}
      <StyledSection $bg={CSSVars.gray50}>
        <StyledInner>
          <StyledSectionLabel>WHY COMMON NINJA</StyledSectionLabel>
          <StyledSectionHeading>
            Built for results, not just features
          </StyledSectionHeading>
          <StyledBenefitsList>
            {benefits.map((benefit, i) => (
              <StyledBenefitItem key={i}>
                <span className="check">&#10003;</span>
                <p>{benefit}</p>
              </StyledBenefitItem>
            ))}
          </StyledBenefitsList>
        </StyledInner>
      </StyledSection>

      {/* Platform compatibility */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>WORKS EVERYWHERE</StyledSectionLabel>
          <StyledSectionHeading>Embed on any platform</StyledSectionHeading>
          <StyledSectionIntro>
            Common Ninja widgets work out-of-the-box with every major website
            builder and e-commerce platform. One embed code, every platform.
          </StyledSectionIntro>
        </StyledInner>
        <StyledPlatformCarousel>
          <div className="carousel-track">
            {[...PLATFORM_LOGOS, ...PLATFORM_LOGOS].map((p, i) => (
              <Link
                key={`${p.slug}-${i}`}
                href={`/platforms/${p.slug}`}
                className="logo-item"
              >
                <Image
                  src={`/assets/platforms/${p.file}`}
                  alt={`${p.name} logo`}
                  width={p.width}
                  height={36}
                />
              </Link>
            ))}
          </div>
        </StyledPlatformCarousel>
        <StyledInner>
          <StyledSectionCta as={Link} href="/platforms" $center>
            See All Platforms
          </StyledSectionCta>
        </StyledInner>
      </StyledSection>

      {/* CTA Banner */}
      <StyledSection $bg={CSSVars.gray50}>
        <StyledCtaBanner>
          <h2>{ctaHeading}</h2>
          <p>
            All Common Ninja widgets are free to start. No coding required, no
            credit card needed.
          </p>
          <Link href="/widgets">Browse All Widgets</Link>
        </StyledCtaBanner>
      </StyledSection>
    </>
  );
};
